import React, { useContext, useEffect, useRef, useState } from 'react';
import PageLayout from 'layout/page/Page.layout';
import { Container } from 'layout/page/Page.layout.styled';
import { QuestionnaireMainWrapper } from 'pages/questionnaire/Questionnaire.styled';
import { SwiperSlide } from 'swiper/react';
import Slider from 'components/slider/Slider';
import { Swiper as SwiperClass } from 'swiper/types';
import QuestionnaireHome, { QuestionnaireHomeProps } from 'pages/questionnaire/slides/home/QuestionnaireHome';
import QuestionnaireProfileForm, { IQuestionnaireProfileForm } from 'pages/questionnaire/slides/profile/QuestionnaireProfileForm';
import useAPIQuestionnaire, { IQuestionnaireData } from 'hooks/api/useAPIQuestionnaire';
import { UserContext, UserContextProps } from 'contexts/UserContext';
import QuestionnaireMain, { QuestionnaireMainProps } from 'pages/questionnaire/slides/questionnaire/QuestionnaireMain';
import QuestionnaireEnd from 'pages/questionnaire/slides/end/QuestionnaireEnd';
import IpsativeQuestionnaireMain, { IpsativeQuestionnaireMainProps } from 'pages/questionnaire/slides/ipsative/IpsativeQuestionnaireMain';
import GradualQuestionnaireMain, { GradualQuestionnaireMainProps } from 'pages/questionnaire/slides/gradual/GradualQuestionnaireMain';
import DepuisQuandQuestionnaireMain, { DepuisQuandQuestionnaireMainProps } from 'pages/questionnaire/slides/depuisquand/DepuisQuandQuestionnaireMain';
import useUpdateChecker from 'hooks/useUpdateChecker';
import { IUser, Questionnaire as QuestionnaireType } from 'types/app';
import {
  getGlobalProgression,
  isQuestionnaireEndPending,
  setAnsweredQuestionnaireCookie,
} from 'helpers/utils';
import { ROUTES } from 'constants/routes';
// === TRIMA INTEGRATION ===
import { useTranslation } from 'react-i18next';
import { useDeviceSelectors } from 'react-device-detect';
import { checkTrimaEligibility } from 'pages/questionnaire-trima/eligibility';
import TrimaInvitation from 'pages/questionnaire-trima/slides/invitation/TrimaInvitation';
// === END TRIMA INTEGRATION ===

type SlideData =
  | { type: 'HOME'; props: QuestionnaireHomeProps }
  | { type: 'PROFILE'; props: Pick<IQuestionnaireProfileForm, 'activities' | 'onProfileUpdate'> }
  | { type: 'IPSATIVE'; props: Omit<IpsativeQuestionnaireMainProps, 'questionnaireProgression' | 'onProgressUpdate'> }
  | { type: 'MAIN'; props: Omit<QuestionnaireMainProps, 'questionnaireProgression' | 'onProgressUpdate'> }
  | { type: 'GRADUAL'; props: Omit<GradualQuestionnaireMainProps, 'questionnaireProgression' | 'onProgressUpdate'> }
  | { type: 'DEPUIS_QUAND'; props: Omit<DepuisQuandQuestionnaireMainProps, 'onProgressUpdate'> }
  | { type: 'END' }
  // === TRIMA INTEGRATION ===
  | { type: 'TRIMA_INVITATION' }
  // === END TRIMA INTEGRATION ===
  ;

const Questionnaire: React.FC = () => {
  useUpdateChecker();
  const { user } = useContext(UserContext) as UserContextProps;
  const userRef = useRef(user);
  // === TRIMA INTEGRATION ===
  const { i18n } = useTranslation();
  const [deviceSelectors] = useDeviceSelectors(window.navigator.userAgent);
  deviceSelectors;
  const isMobileDevice = true;
  // === END TRIMA INTEGRATION ===

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const { loadQuestionnaireData } = useAPIQuestionnaire();
  const swiperInstance = useRef<SwiperClass>();
  const [slidesData, setSlidesData] = useState<Array<SlideData>>([]);
  const [topicNumbers, setTopicNumbers] = useState<number>(0);
  const sectionIds = useRef<number[]>([]);
  const currentChapterSectionNumber = useRef<number>(0);
  const isInitialMount = useRef(true);
  const [progressData, setProgressData] = useState<Record<string, { current: number, total: number }>>({
    main: { current: 0, total: 0 },
    ipsative: { current: 0, total: 0 },
    gradual: { current: 0, total: 0 },
    depuisQuand: { current: 0, total: 0 },
  });

  const handleProgressUpdate = (id: string, current: number, total: number) => {
    setProgressData((prev) => {
      // Only update if changed prevents infinite loop if effect calls it repeatedly
      if (prev[id]?.current === current && prev[id]?.total === total) return prev;
      return {
        ...prev,
        [id]: { current, total },
      };
    });
  };

  /**
   * This function, `insertTransitionsAtBeginning`, processes an array of questions and inserts transition objects at the beginning of each new section.
   * 
   * It takes an array of questions (`originalQuestionsArray`) as input and returns a new array with transition objects added.
   * 
   * The function initializes several variables:
   * - `notPassedChapterSectionIds`: an array to track section IDs that have not been fully answered.
   * - `tmpCurrentChapterSectionNumber`: a temporary counter for the current chapter section number.
   * - `resultArray`: an array to store the processed questions and transition objects.
   * - `tmpTopicNumbers`: a temporary counter for the number of topics, initialized with the current `topicNumbers`.
   * - `nextSectionLabel`: an object to hold the label of the next section.
   * 
   * The function iterates over each question in the input array. For each question, it checks if the current question is the last in its section.
   * If so, it updates `nextSectionLabel` with the label of the next section.
   * 
   * A transition object is created with default values and the `nextSectionLabel`. This object is inserted at the beginning of each new section.
   * 
   * The function also tracks sections that have not been fully answered using `notPassedChapterSectionIds`. If a question has a response and its section ID is in this list, the section ID is removed from the list, and the chapter section number is incremented.
   * 
   * Finally, the function updates the `currentChapterSectionNumber` and `topicNumbers` with the temporary values and returns the `resultArray`.
   */
  function insertTransitionsAtBeginning(originalQuestionsArray: QuestionnaireType.IQuestion[]) {
    let notPassedChapterSectionIds: number[] = [];
    const tmpSectionIds: number[] = [];
    let tmpCurrentChapterSectionNumber = 0;
    const resultArray: any[] = [];
    let tmpTopicNumbers = topicNumbers;

    let nextSectionLabel = {};

    originalQuestionsArray.forEach((currentItem, index, array) => {
      if (index < array.length - 1 && currentItem.sectionId !== array[index + 1].sectionId) {
        nextSectionLabel = array[index + 1].sectionLabel;
      }

      if (index === 0 && originalQuestionsArray.length > 0) {
        nextSectionLabel = array[index + 1].sectionLabel;
      }

      const transitionObject: QuestionnaireType.IQuestion = {
        id: -1,
        sectionId: -1,
        sectionLabel: nextSectionLabel,
        label: {},
        order: -1,
        responseMax: -1,
        responseMin: -1,
        response: -1,
      };

      if (index === 0 || currentItem.sectionId !== array[index - 1].sectionId) {
        resultArray.push(transitionObject);
        tmpSectionIds.push(currentItem.sectionId);
        notPassedChapterSectionIds.push(currentItem.sectionId);
        tmpTopicNumbers += 1;
      }

      resultArray.push(currentItem);
      if (currentItem.response && notPassedChapterSectionIds.includes(currentItem.sectionId)) {
        notPassedChapterSectionIds = notPassedChapterSectionIds.filter(
          (id) => id !== currentItem.sectionId,
        );
        tmpCurrentChapterSectionNumber += 1;
      }
    });
    sectionIds.current = tmpSectionIds;
    currentChapterSectionNumber.current = tmpCurrentChapterSectionNumber;
    setTopicNumbers(tmpTopicNumbers);
    return resultArray;
  }

  const findUnansweredQuestionsBeforeCurrentQuestion = (questions: QuestionnaireType.IQuestion[]): {
    unansweredQuestions: QuestionnaireType.IQuestion[];
    currentQuestionIndex: number;
  } => {
    const lastResponseIndex = questions.reduce((lastIndex, question, index) => {
      return question.response !== undefined ? index : lastIndex;
    }, -1);
    const lastResponse = questions[lastResponseIndex];
    if (!lastResponse) {
      return {
        unansweredQuestions: [],
        currentQuestionIndex: 0
      };
    };
    const unansweredQuestions = questions.filter(question => question.order < lastResponse.order && !question.response);
    return {
      unansweredQuestions,
      currentQuestionIndex: lastResponseIndex + 1,
    };
  };

  const createSlides = async (qData: IQuestionnaireData, reloadFn?: (user?: any) => Promise<void>, skipHome?: boolean, overrideUser?: typeof user) => {
    // console.log('🔵 [createSlides] Starting slide creation', { skipHome, qData });
    const slides: Array<SlideData> = [];
    if (!skipHome && qData.textIntro) {
      // console.log('✅ [createSlides] Adding HOME slide');
      slides.push({ type: 'HOME', props: { textIntro: qData.textIntro } });
    } else {
      // console.log('⏭️  [createSlides] Skipping HOME slide', { skipHome, hasTextIntro: !!qData.textIntro });
    }

    const currentUser = overrideUser ?? userRef.current;
    // console.log('🔍 [createSlides] Checking PROFILE requirements', {
    //   hasActivity: !!currentUser?.profileInfo?.activity,
    //   hasCivility: !!currentUser?.civilityInDatabase,
    //   usingOverrideUser: !!overrideUser
    // });
    if (!currentUser?.profileInfo?.activity || !currentUser?.civilityInDatabase) {
      slides.push({
        type: 'PROFILE',
        props: {
          activities: qData.activities ?? [],
          onProfileUpdate: (newUser) => {
            reloadFn && reloadFn(newUser);
          },
        }
      });
    } else {
      // console.log('⏭️  [createSlides] Skipping PROFILE slide (already complete)');
    }

    // Add ipsative questionnaire as the first questionnaire step
    // console.log('🔍 [createSlides] Checking IPSATIVE requirements', {
    //   hasIpsative: !!qData?.ipsativeQuestionnaire,
    //   progression: qData?.ipsativeQuestionnaire?.progression,
    //   typeGroup: currentUser?.profileInfo?.typeGroup
    // });
    if (qData?.ipsativeQuestionnaire && qData?.ipsativeQuestionnaire?.progression < 100 && currentUser?.profileInfo?.typeGroup !== 'etudiant') {
      // console.log('✅ [createSlides] Adding IPSATIVE slide');
      slides.push({
        type: 'IPSATIVE',
        props: {
          questionnaireData: qData.ipsativeQuestionnaire,
        }
      });
    } else {
      // console.log('⏭️  [createSlides] Skipping IPSATIVE slide');
    }

    // console.log('🔍 [createSlides] Checking MAIN questionnaire', {
    //   hasQuestionnaire: !!qData.questionnaire,
    //   questionsCount: qData.questionnaire?.questions?.length,
    //   progression: qData.questionnaire?.progression
    // });
    if (!qData.questionnaire || qData.questionnaire.questions.length === 0) {
      // console.log('❌ [createSlides] No MAIN questionnaire data, exiting early');
      return;
    }

    const resp = findUnansweredQuestionsBeforeCurrentQuestion(qData.questionnaire?.questions ?? []);
    // console.log('🔍 [createSlides] Unanswered questions analysis', {
    //   unansweredCount: resp?.unansweredQuestions?.length,
    //   currentQuestionIndex: resp?.currentQuestionIndex
    // });
    if (resp?.unansweredQuestions && resp.unansweredQuestions.length > 0) {
      // console.log('✅ [createSlides] Adding MAIN slide (with unanswered questions)');
      const { unansweredQuestions, currentQuestionIndex } = resp;
      const startArray = (qData.questionnaire.questions.slice(0, currentQuestionIndex).filter(question => {
        return !unansweredQuestions.some(unanswered => unanswered.id === question.id);
      }));
      const endArray = insertTransitionsAtBeginning(qData.questionnaire.questions.slice(currentQuestionIndex).filter(question => {
        return !unansweredQuestions.some(unanswered => unanswered.id === question.id);
      }));

      let removeFirstTransitionForEndArray = false;

      if (unansweredQuestions?.length > 0 && endArray?.length > 0) {
        if (unansweredQuestions[0].sectionId === endArray[1].sectionId) {
          removeFirstTransitionForEndArray = true;
        }

        if (removeFirstTransitionForEndArray) {
          endArray.shift();
        }
      }

      const modifiedArray = [...insertTransitionsAtBeginning(startArray), ...unansweredQuestions, ...(endArray)];
      const modifiedArrayAllQuestions = insertTransitionsAtBeginning(qData.questionnaire.questions);
      slides.push({
        type: 'MAIN',
        props: {
          q: {
            ...qData.questionnaire,
            questions: [...modifiedArray],
            currentQuestionIndex: [...modifiedArray].findIndex(question => question.id === unansweredQuestions[0].id),
            progression: qData.questionnaire.progression,
            allQuestions: [...modifiedArrayAllQuestions]
          },
          topicNumbers: 0,
        }
      });
    } else {
      // console.log('🔍 [createSlides] No unanswered questions, checking progression');
      if (qData.questionnaire && qData.questionnaire?.progression < 100) {
        // console.log('✅ [createSlides] Adding MAIN slide (progression < 100)');
        const modifiedArray = insertTransitionsAtBeginning(qData.questionnaire.questions);
        // console.log('🔍 [createSlides] Questions after inserting transitions', { modifiedArray });
        slides.push({
          type: 'MAIN',
          props: {
            q: {
              ...qData.questionnaire,
              questions: [...modifiedArray],
              currentQuestionIndex:
                qData.questionnaire.currentQuestionIndex + currentChapterSectionNumber.current,
            },
            topicNumbers: 0,
          }
        });
      }
    }

    // Check if everything is complete
    const isMainComplete = qData.questionnaire?.progression === 100;
    const isGradualComplete = !qData.gradualQuestionnaire || qData.gradualQuestionnaire?.progression === 100;
    const isDepuisQuandComplete = !qData.depuisQuandQuestionnaire || qData.depuisQuandQuestionnaire?.progression === 100;
    // console.log('🔍 [createSlides] Completion status', { isDepuisQuandComplete });

    // Add gradual questionnaire after main questionnaire
    // console.log('🔍 [createSlides] Checking GRADUAL requirements', {
    //   hasGradual: !!qData?.gradualQuestionnaire,
    //   gradualProgression: qData?.gradualQuestionnaire?.progression
    // });
    if (qData?.gradualQuestionnaire && qData?.gradualQuestionnaire?.progression < 100) {
      // console.log('✅ [createSlides] Adding GRADUAL slide');
      slides.push({
        type: 'GRADUAL',
        props: {
          questionnaireData: qData.gradualQuestionnaire,
        }
      });
    } else {
      // console.log('⏭️  [createSlides] Skipping GRADUAL slide', {
      //   reason: !qData?.gradualQuestionnaire ? 'No gradual data' : 'Gradual already complete'
      // });
    }

    // Add depuis quand questionnaire after gradual questionnaire
    // console.log('🔍 [createSlides] Checking DEPUIS_QUAND requirements', {
    //   hasDepuisQuand: !!qData?.depuisQuandQuestionnaire,
    //   depuisQuandProgression: qData?.depuisQuandQuestionnaire?.progression
    // });
    if (qData?.depuisQuandQuestionnaire && qData?.depuisQuandQuestionnaire?.progression < 100) {
      // console.log('✅ [createSlides] Adding DEPUIS_QUAND slide');
      slides.push({
        type: 'DEPUIS_QUAND',
        props: {
          questionnaireData: qData.depuisQuandQuestionnaire,
        }
      });
    } else {
      // console.log('⏭️  [createSlides] Skipping DEPUIS_QUAND slide', {
      //   reason: !qData?.depuisQuandQuestionnaire ? 'No depuis quand data' : 'Depuis quand already complete'
      // });
    }

    if (isMainComplete && isGradualComplete && isDepuisQuandComplete) {
      // console.log('🎉 [createSlides] All questionnaires complete!');
      // === TRIMA INTEGRATION ===
      // If the user is eligible for Trima, we DON'T auto-redirect to /.
      // Instead, we let the END slide render normally, and we append a
      // TRIMA_INVITATION slide right after. A new button on END (tagged
      // "TRIMA INTEGRATION" inside QuestionnaireEnd.tsx) lets the user
      // swipe to the invitation.
      const trimaEligibility = checkTrimaEligibility({
        user: currentUser,
        language: i18n.language,
        isMobile: isMobileDevice,
        isComColorsComplete: true, // we just verified it above
      });
      // The END slide stays visible across reloads until the user leaves it
      // through one of its buttons (see InstallAppTuto). Only then do we
      // redirect straight to the home page.
      const endPending = isQuestionnaireEndPending(currentUser?.id);
      if (!trimaEligibility.isEligible && !endPending) {
        setAnsweredQuestionnaireCookie();
        window.location.href = ROUTES.INDEX;
      }
      // If eligible: fall through without redirecting. The TRIMA_INVITATION
      // slide is pushed below, just after END.
      // === END TRIMA INTEGRATION ===
    }

    // console.log('✅ [createSlides] Adding END slide');
    slides.push({ type: 'END' });

    // === TRIMA INTEGRATION ===
    // Append the Trima invitation slide right after END, only when:
    //   - the ComColors questionnaire is fully complete
    //   - the user is eligible (mobile + FR + logged in)
    // The END slide gets a conditional CTA (see QuestionnaireEnd.tsx) that
    // calls swiper.slideNext() to reach this slide.
    if (isMainComplete && isGradualComplete && isDepuisQuandComplete) {
      const trimaEligibility = checkTrimaEligibility({
        user: currentUser,
        language: i18n.language,
        isMobile: isMobileDevice,
        isComColorsComplete: true,
      });
      if (trimaEligibility.isEligible) {
        slides.push({ type: 'TRIMA_INVITATION' });
      }
    }
    // === END TRIMA INTEGRATION ===

    // Nothing left to answer (e.g. reload on the end screen): skip the intro
    // slide and show the end screen directly.
    const hasPartToAnswer = slides.some(
      (slide) => slide.type !== 'HOME' && slide.type !== 'END' && slide.type !== 'TRIMA_INVITATION',
    );
    if (!hasPartToAnswer && slides[0]?.type === 'HOME') {
      slides.shift();
    }

    const getQuestionnaireCurrentIndex = (questionnaire: any) => {
      if (questionnaire?.progression === 100) {
        return questionnaire?.questions?.length;
      }
      return questionnaire?.currentQuestionIndex;
    };
    const mainCurrentQuestionIndex = getQuestionnaireCurrentIndex(qData.questionnaire);
    const gradualCurrentQuestionIndex = getQuestionnaireCurrentIndex(qData.gradualQuestionnaire);
    const ipsativeCurrentQuestionIndex = getQuestionnaireCurrentIndex(qData.ipsativeQuestionnaire);
    const tmpProgression = {
      main: {
        current: mainCurrentQuestionIndex,
        total: qData.questionnaire?.questions.length,
      },
      gradual: {
        current: gradualCurrentQuestionIndex,
        total: qData.gradualQuestionnaire?.questions.length ?? 0,
      },
      ipsative: {
        current: ipsativeCurrentQuestionIndex,
        total: qData.ipsativeQuestionnaire?.questions.length ?? 0,
      },
    };
    setProgressData(tmpProgression);
    // console.log('🎯 [createSlides] Final slides array', {
    //   slidesCount: slides.length,
    //   slideTypes: slides.map(s => s.type),
    //   progression: tmpProgression
    // });
    setSlidesData(slides);
    return;
  };

  const loadData = async (skipHome?: boolean, overrideUser?: IUser) => {
    // console.log('🔄 [loadData] Starting data load', { skipHome });
    try {
      const currentUser = overrideUser ?? user;
      if (!currentUser) return;
      await createSlides(await loadQuestionnaireData(currentUser), (overrideUser) => loadData(true, overrideUser), skipHome, currentUser);
    } catch (e) {
      console.error('❌ [loadData] Error:', e);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Reset swiper to first slide when slides are rebuilt (e.g., after profile update)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else if (swiperInstance.current && slidesData.length > 0) {
      // Slides have been updated after initial mount, reset to first slide
      swiperInstance.current.slideTo(0, 0); // 0 speed for instant transition
    }
  }, [slidesData]);

  return (
    <PageLayout
      headerProps={{ showLogo: true, hideNotification: true, showLangSwitcher: true }}
      className="page-layout-questionnaire"
    >
      <QuestionnaireMainWrapper>
        <Container padding={0} className="app-page">
          <Slider
            onInit={(swiper: SwiperClass) => {
              swiperInstance.current = swiper;
            }}
            allowTouchMove={false}
          >
            {slidesData.map((slide, index) => (
              <SwiperSlide key={`slide-${index}`}>
                {slide.type === 'HOME' && <QuestionnaireHome {...slide.props} />}
                {slide.type === 'PROFILE' && (
                  <QuestionnaireProfileForm
                    {...slide.props}
                    currentActivity={user?.profileInfo?.activity ?? undefined}
                    currentBirthYear={user?.profileInfo?.birthYear}
                    currentCivility={user?.civilityInDatabase}
                  />
                )}
                {slide.type === 'IPSATIVE' && (
                  <IpsativeQuestionnaireMain
                    {...slide.props}
                    onProgressUpdate={handleProgressUpdate}
                    questionnaireProgression={getGlobalProgression(progressData)}
                  />
                )}
                {slide.type === 'MAIN' && (
                  <QuestionnaireMain
                    {...slide.props}
                    onProgressUpdate={handleProgressUpdate}
                    questionnaireProgression={getGlobalProgression(progressData)}
                  />
                )}
                {slide.type === 'GRADUAL' && (
                  <GradualQuestionnaireMain
                    {...slide.props}
                    onProgressUpdate={handleProgressUpdate}
                    questionnaireProgression={getGlobalProgression(progressData)}
                  />
                )}
                {slide.type === 'DEPUIS_QUAND' && (
                  <DepuisQuandQuestionnaireMain
                    {...slide.props}
                    onProgressUpdate={handleProgressUpdate}
                  />
                )}
                {slide.type === 'END' && (
                  <QuestionnaireEnd
                    // === TRIMA INTEGRATION ===
                    // Show the "Discover my skills assessment" button only when
                    // the next slide is the Trima invitation (i.e. eligible user).
                    showTrimaCta={slidesData[index + 1]?.type === 'TRIMA_INVITATION'}
                    // === END TRIMA INTEGRATION ===
                  />
                )}
                {/* === TRIMA INTEGRATION === */}
                {slide.type === 'TRIMA_INVITATION' && (
                  <TrimaInvitation
                    // onAccept default = navigate to /questionnaire-competences
                    // onRefuse default = clear storage + go home
                  />
                )}
                {/* === END TRIMA INTEGRATION === */}
              </SwiperSlide>
            ))}
          </Slider>
        </Container>
      </QuestionnaireMainWrapper>
    </PageLayout>
  );
};

export default Questionnaire;
